import { AssetManager } from 'components/sharpr/assetManager/AssetManager';
import { ApiResourcify } from 'components/sharpr/api/ApiResourcify';
import { htmlExtractText } from 'components/sharpr/html/extractText/htmlExtractText';
import randomBytes from 'randombytes';

export function MsTeamsPostRoute($stateProvider) {
	'ngInject';
	$stateProvider.state('hub.loggedin.MsTeamsPost', {
		url: '/msTeamsPost',
		controllerAs: 'vm',
		controller: MsTeamsPostCtrl,
		templateUrl: '/ng/hub/PushToMsTeams/MsTeamsPostCtrl.html?' + window.BUILD,
		resolve: {
			style: () => AssetManager.css('/ng/hub/PushToMsTeams/MsTeamsPostCtrl.css'),
			$title: () => 'Push to Microsoft Teams',
		},
	});
}

export class MsTeamsPostCtrl {
	constructor(Identity, Logger, modal, Api, $stateParams, $scope, MsalAuth) {
		'ngInject';
		this.Identity = Identity;
		this.Logger = Logger;
		this.modal = modal;
		this.$stateParams = $stateParams;
		this.post = $stateParams.post;
		this.accessToken = $stateParams.accessToken || null;
		this.Api = Api;
		this.MsalAuth = MsalAuth;
		this.$scope = $scope;
		this.init();
	}

	/*
	 * Restarts the entire process. Exposed when an unexpected error occurs
	 */
	async retry() {
		await this.init();
	}

	/*
	 * Initialization kick off
	 */
	async init() {
		try {
			await this.initVars();
			this.postSummary = this.getPostSummary();

			// delay the next init phase to give the user time to see what's going on
			setTimeout(() => {
				this.init2();
			}, 1500);
		} catch (err) {
			this.reportInitError(err);
		}
	}

	/*
	 * Variable initialization
	 */
	async initVars() {
		this.success = false;
		this.isAuthenticated = !!this.accessToken;
		this.initErr = '';
		this.otherErr = null;
		this.targetTeamId = null;
		this.targetChannelId = null;
		this.teams = [];
		this.channels = [];
		this.postSummary = '';
		this.extraMsg = '';
	}

	/*
	 * Second stage of initialization
	 */
	async init2() {
		if (!this.isAuthenticated) {
			return;
		}
		this.teams = await this.initTeams();
		this.$scope.$digest();
	}

	/*
	 * Constructs the post summary
	 * @return string
	 */
	getPostSummary() {
		let ret = `#${this.post.id}`;
		if (this.post.headline) {
			ret = this.post.headline;
		}
		return ret;
	}

	/*
	 * Handler for manually-initiated auth via popup
	 */
	async getAuthManually() {
		try {
			this.initErr = '';
			this.accessToken = await this.MsalAuth.getAccessToken('consent');
			this.isAuthenticated = !!this.accessToken;
			this.$scope.$digest();
			await this.init2();
			this.$scope.$digest();
		} catch (err) {
			this.reportInitError(err);
		}
	}

	/*
	 * Pulls list of teams for the auth'd user
	 * @return Array[Object]
	 * @throws
	 */
	async initTeams() {
		const options = this.getFetchOptions();
		const url = 'https://graph.microsoft.com/v1.0/me/joinedTeams';
		const resp = await fetch(url, options);
		const respJson = await resp.json();
		const err = 'No teams found';
		let teams = [];

		if (typeof respJson.error !== 'undefined') {
			throw respJson.error;
		}
		if (!respJson || typeof respJson.value === 'undefined' || !respJson.value.length) {
			throw err;
		}

		for (let i = 0; i < respJson.value.length; i++) {
			const team = respJson.value[i];
			if (!this.validateInitTeam(team)) {
				continue;
			}
			teams.push({
				id: team.id,
				name: team.displayName,
			});
		}
		if (!teams.length) {
			throw err;
		}

		return teams;
	}

	/*
	 * Constructs the Graph api's http-request config
	 * @return Object
	 */
	getFetchOptions() {
		const headers = new Headers();
		const bearer = 'Bearer ' + this.accessToken;
		headers.append('Authorization', bearer);
		headers.append('Accept', 'application/json');
		const options = {
			method: 'GET',
			headers: headers,
		};
		return options;
	}

	/*
	 * Validates a team's details to ensure we're only working
	 * with teams that we can push to
	 * @param team Object
	 * @return Bool
	 */
	validateInitTeam(team) {
		if (
			(typeof team.isArchived !== 'undefined' && team.isArchived) ||
			typeof team.id === 'undefined' ||
			!team.id ||
			typeof team.displayName === 'undefined' ||
			!team.displayName
		) {
			return false;
		}
		return true;
	}

	/*
	 * Reports an initialization error. Informs the user and logs the error.
	 * @param err Object|String
	 */
	reportInitError(err) {
		if (typeof err === 'object') {
			err = this.extractErrorMsg(err);
		}
		this.initErr = err;
		this.Logger.write('PUSH_TO_MSTEAMS_ERROR', `Initialization error - ${err}`);
	}

	/*
	 * Extracts the reportable portion of an error object
	 * @param Object
	 * @return String
	 */
	extractErrorMsg(err) {
		let ret = '';
		let hasMsg = false;
		if (typeof err.errorMessage !== 'undefined') {
			ret = err.errorMessage;
			hasMsg = true;
		} else if (typeof err.message !== 'undefined') {
			ret = err.message;
			hasMsg = true;
		}
		let code = '';
		if (typeof err.errorCode !== 'undefined') {
			code = err.errorCode;
		} else if (typeof err.code !== 'undefined') {
			code = err.code;
		}
		if (code) {
			if (hasMsg) {
				ret += ' (';
			}
			ret += code;
			if (hasMsg) {
				ret += ')';
			}
		}
		return ret;
	}

	/*
	 * Handler for team selection
	 */
	async handleTeamSelect() {
		try {
			this.targetChannelId = null;
			this.channels = [];
			this.$scope.$digest();
			this.channels = await this.initChannels();
			this.$scope.$digest();
		} catch (err) {
			this.reportInitError(err);
		}
	}

	/*
	 * Pulls list of user's channels for teams
	 * @return Array
	 * @throws
	 */
	async initChannels() {
		const url = `https://graph.microsoft.com/v1.0/teams/${this.targetTeamId}/channels`;
		const options = this.getFetchOptions();
		const resp = await fetch(url, options);
		const respJson = await resp.json();
		const err = 'No channels found';
		let channels = [];

		if (typeof respJson.error !== 'undefined') {
			throw respJson.error;
		}
		if (!respJson || typeof respJson.value === 'undefined' || !respJson.value.length) {
			throw err;
		}

		for (let i = 0; i < respJson.value.length; i++) {
			const channel = respJson.value[i];
			if (!this.validateInitChannel(channel)) {
				continue;
			}
			channels.push({
				id: channel.id,
				name: channel.displayName,
			});
		}
		if (!channels.length) {
			throw err;
		}

		return channels;
	}

	/*
	 * Validates a channel to ensure that we only display those
	 * to which we can push
	 * @param channel Object
	 * @return Bool
	 */
	validateInitChannel(channel) {
		if (
			typeof channel.id === 'undefined' ||
			!channel.id ||
			typeof channel.displayName === 'undefined' ||
			!channel.displayName
		) {
			return false;
		}
		return true;
	}

	/*
	 * Handler for pushing the post to teams
	 */
	async handleSend() {
		try {
			await this.pushToTeams();
			this.$scope.$digest();
		} catch (err) {
			this.reportOtherError('Send error', err);
		}
	}

	/*
	 * Pushes the post into teams as a formatted adaptive card
	 * @throws
	 */
	async pushToTeams() {
		const url = `https://graph.microsoft.com/v1.0/teams/${this.targetTeamId}/channels/${this.targetChannelId}/messages`;
		const cardId = randomBytes(16).toString('hex');
		const cardBody = this.getCardBody();
		let contentBody = '<attachment id="' + cardId + '"></attachment>';
		if (this.extraMsg) {
			contentBody = this.extraMsg + '<br>' + contentBody;
		}

		let options = this.getFetchOptions();
		options.method = 'POST';
		options.body = JSON.stringify({
			subject: null,
			body: {
				contentType: 'html',
				content: contentBody,
			},
			attachments: [
				{
					id: cardId,
					contentType: 'application/vnd.microsoft.card.adaptive',
					contentUrl: null,
					content: JSON.stringify(cardBody),
					name: null,
					thumbnailUrl: null,
				},
			],
		});

		options.headers.append('Content-type', 'application/json');
		const resp = await fetch(url, options);
		const respJson = await resp.json();
		if (!respJson || typeof respJson.id === 'undefined') {
			throw 'Unsuccessful push';
		}
		this.success = true;
	}

	/*
	 * Constructs the body of the adaptive card to be pushed
	 * @return Object
	 */
	getCardBody() {
		const imageUrl = this.getImageUrl();
		const MAX_LENGTH_SUMMARY = 300;
		let summary = htmlExtractText(this.post.rich_text_body);
		let publishedBy = 'Not Available';

		if (summary.length > MAX_LENGTH_SUMMARY) {
			summary = summary.substr(0, MAX_LENGTH_SUMMARY) + '...';
		}

		if (this.post.publishedBy) {
			publishedBy = `${this.post.publishedBy.fname} ${this.post.publishedBy.lname}`;
		}

		const cardBody = {
			body: [
				{
					type: 'Image',
					url: imageUrl,
					size: 'stretch',
				},
				{
					type: 'TextBlock',
					text: this.post.headline,
					weight: 'bolder',
					size: 'large',
				},
				{
					type: 'TextBlock',
					text: summary,
					wrap: true,
				},
				{
					type: 'TextBlock',
					text: `Published by: ${publishedBy}`,
					isSubtle: true,
					size: 'small',
				},
			],
			actions: [
				{
					type: 'Action.OpenUrl',
					title: 'View Post',
					url: `${window.BASE_URL}/p/${this.post.id}`,
				},
			],
		};

		return cardBody;
	}

	/*
	 * Constructs the post's image url.
	 * @return String
	 */
	getImageUrl() {
		let imageUrl = `${window.BASE_URL}/assets/theme/sharpr/missing.png`;

		if (this.post.image && this.post.image.url) {
			imageUrl = this.post.image.url;
		} else if (this.post.attachment_thumbnails && this.post.attachment_thumbnails.length) {
			imageUrl = `${this.post.attachment_thumbnails[0].url}.grid-500x500.png`;
		} else if (this.post.attachment_icons && this.post.attachment_icons.length) {
			imageUrl = `${window.BASE_URL}/assets/img/filetypes/secondary/${this.post.attachment_icons[0]}.png`;
		}

		return imageUrl;
	}

	/*
	 * Reports and logs "other" errors (ie. non-initialization) to the user.
	 */
	reportOtherError(title, err) {
		if (typeof err === 'object') {
			err = this.extractErrorMsg(err);
		}
		this.otherErr = {
			title: title,
			msg: err,
		};
		this.Logger.write('PUSH_TO_MSTEAMS_ERROR', `${title} - ${err}`);
		this.$scope.$digest();
	}

	/*
	 * Initiates a modal close
	 */
	cancel() {
		this.modal.resolveByTitle('Push to Microsoft Teams');
	}
}
